import Layout from '@components/layout'
import { loginPageModuleQuery } from '@data/sanity/queries/page'
import { SanityLoginPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getLoginPage } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type LoginPageProps = BasePageProps & {
  page: SanityLoginPage
  site: SanitySiteFragment
}

const LoginPage = ({ draftMode, locale, page, site }: LoginPageProps) => {
  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={loginPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<LoginPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const loginPage = await getLoginPage(sanityClient, context.locale)

  if (!loginPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(loginPage.page),
      site: getNonNullValues(loginPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default LoginPage
